import { create } from 'zustand';
import analyticsService from '../../services/analytics/AnalyticsService';
import {
    TopProduct,
    LowStockProduct,
    ProductWithViews,
    SalesVsNonsalesData
} from '../../models/Analytics';

// Re-export types for convenience
export type {
    TopProduct,
    LowStockProduct,
    ProductWithViews,
    SalesVsNonsalesData
};

interface ProductAnalyticsState {
    // Top Products
    topProducts: TopProduct[];
    topProductsLoading: boolean;
    topProductsError: string | null;
    
    // Low Stock Products
    lowStockProducts: LowStockProduct[];
    lowStockProductsLoading: boolean;
    lowStockProductsError: string | null;
    
    // Lowest Stock Products
    lowestStockProducts: LowStockProduct[];
    lowestStockProductsLoading: boolean;
    lowestStockProductsError: string | null;
    
    // Products with Views
    productsWithViews: ProductWithViews[];
    productsWithViewsLoading: boolean;
    productsWithViewsError: string | null;
    
    // Sales vs Non-Sales
    salesVsNonsales: SalesVsNonsalesData[];
    salesVsNonsalesLoading: boolean;
    salesVsNonsalesError: string | null;
    
    // Thunks (Async Actions)
    fetchTopProducts: (limit?: number, startDate?: string, endDate?: string) => Promise<void>;
    fetchLowStockProducts: () => Promise<void>;
    fetchLowestStockProducts: (limit?: number) => Promise<void>;
    fetchProductsWithViews: (startDate?: string, endDate?: string) => Promise<void>;
    fetchSalesVsNonsales: (startDate?: string, endDate?: string) => Promise<void>;
    
    // Reset actions
    resetTopProducts: () => void;
    resetLowStockProducts: () => void;
    resetLowestStockProducts: () => void;
    resetProductsWithViews: () => void;
    resetSalesVsNonsales: () => void;
}

export const productAnalyticsStore = create<ProductAnalyticsState>((set) => ({
    // Initial State
    topProducts: [],
    topProductsLoading: false,
    topProductsError: null,
    
    lowStockProducts: [],
    lowStockProductsLoading: false,
    lowStockProductsError: null,
    
    lowestStockProducts: [],
    lowestStockProductsLoading: false,
    lowestStockProductsError: null,
    
    productsWithViews: [],
    productsWithViewsLoading: false,
    productsWithViewsError: null,
    
    salesVsNonsales: [],
    salesVsNonsalesLoading: false,
    salesVsNonsalesError: null,
    
    // Thunks
    fetchTopProducts: async (limit = 10, startDate?: string, endDate?: string) => {
        set({ topProductsLoading: true, topProductsError: null });
        try {
            const result = await analyticsService.getTopProducts(limit, startDate, endDate);
            if (result.success && result.data) {
                set({ 
                    topProducts: result.data,
                    topProductsLoading: false,
                    topProductsError: null
                });
            } else {
                set({ 
                    topProductsLoading: false,
                    topProductsError: result.message || 'Failed to fetch top products'
                });
            }
        } catch (error: any) {
            set({ 
                topProductsLoading: false,
                topProductsError: error.message || 'Failed to fetch top products'
            });
        }
    },
    
    fetchLowStockProducts: async () => {
        set({ lowStockProductsLoading: true, lowStockProductsError: null });
        try {
            const result = await analyticsService.getLowStockProducts();
            if (result.success && result.data) {
                set({ 
                    lowStockProducts: result.data,
                    lowStockProductsLoading: false,
                    lowStockProductsError: null
                });
            } else {
                set({ 
                    lowStockProductsLoading: false,
                    lowStockProductsError: result.message || 'Failed to fetch low stock products'
                });
            }
        } catch (error: any) {
            set({ 
                lowStockProductsLoading: false,
                lowStockProductsError: error.message || 'Failed to fetch low stock products'
            });
        }
    },
    
    fetchLowestStockProducts: async (limit = 10) => {
        set({ lowestStockProductsLoading: true, lowestStockProductsError: null });
        try {
            const result = await analyticsService.getLowestStockProducts(limit);
            if (result.success && result.data) {
                set({ 
                    lowestStockProducts: result.data,
                    lowestStockProductsLoading: false,
                    lowestStockProductsError: null
                });
            } else {
                set({ 
                    lowestStockProductsLoading: false,
                    lowestStockProductsError: result.message || 'Failed to fetch lowest stock products'
                });
            }
        } catch (error: any) {
            set({ 
                lowestStockProductsLoading: false,
                lowestStockProductsError: error.message || 'Failed to fetch lowest stock products'
            });
        }
    },
    
    fetchProductsWithViews: async (startDate?: string, endDate?: string) => {
        set({ productsWithViewsLoading: true, productsWithViewsError: null });
        try {
            const result = await analyticsService.getProductsWithViews(startDate, endDate);
            if (result.success && result.data) {
                set({ 
                    productsWithViews: result.data,
                    productsWithViewsLoading: false,
                    productsWithViewsError: null
                });
            } else {
                set({ 
                    productsWithViewsLoading: false,
                    productsWithViewsError: result.message || 'Failed to fetch products with views'
                });
            }
        } catch (error: any) {
            set({ 
                productsWithViewsLoading: false,
                productsWithViewsError: error.message || 'Failed to fetch products with views'
            });
        }
    },
    
    fetchSalesVsNonsales: async (startDate?: string, endDate?: string) => {
        set({ salesVsNonsalesLoading: true, salesVsNonsalesError: null });
        try {
            const result = await analyticsService.getSalesVsNonsales(startDate, endDate);
            if (result.success && result.data) {
                set({ 
                    salesVsNonsales: result.data,
                    salesVsNonsalesLoading: false,
                    salesVsNonsalesError: null
                });
            } else {
                set({ 
                    salesVsNonsalesLoading: false,
                    salesVsNonsalesError: result.message || 'Failed to fetch sales vs non-sales data'
                });
            }
        } catch (error: any) {
            set({ 
                salesVsNonsalesLoading: false,
                salesVsNonsalesError: error.message || 'Failed to fetch sales vs non-sales data'
            });
        }
    },
    
    // Reset actions
    resetTopProducts: () => set({ 
        topProducts: [], 
        topProductsLoading: false, 
        topProductsError: null 
    }),
    
    resetLowStockProducts: () => set({ 
        lowStockProducts: [], 
        lowStockProductsLoading: false, 
        lowStockProductsError: null 
    }),
    
    resetLowestStockProducts: () => set({ 
        lowestStockProducts: [], 
        lowestStockProductsLoading: false, 
        lowestStockProductsError: null 
    }),
    
    resetProductsWithViews: () => set({ 
        productsWithViews: [], 
        productsWithViewsLoading: false, 
        productsWithViewsError: null 
    }),
    
    resetSalesVsNonsales: () => set({ 
        salesVsNonsales: [], 
        salesVsNonsalesLoading: false, 
        salesVsNonsalesError: null 
    }),
}));

